import {
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Flag,
  Presentation,
  RotateCcw,
} from "lucide-react";
import { useGetList, useStore, useTranslate } from "ra-core";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import { useEsAdmin } from "../datos/useEsAdmin";
import { useResponsables } from "../datos/useResponsables";
import { CampanaAvisos } from "../equipo/CampanaAvisos";
import { filtrarPorPersonas } from "../dominio/equipo";
import { FiltroPersonasSelect } from "../equipo/FiltroPersonasSelect";
import { useFiltroPersonas } from "../equipo/useFiltroPersonas";
import {
  agruparAgenda,
  elementosAgenda,
  GRUPOS_AGENDA,
  type ElementoAgenda,
  type TipoElementoAgenda,
} from "../dominio/agenda";
import {
  agruparPorDia,
  desplazar,
  diasDeLaSemana,
  semanasDelMes,
  type VistaCalendario,
} from "../dominio/calendario";
import { aFechaISO, diaDe, diasEntre, inicioDelDia } from "../dominio/fechas";
import type { Negocio } from "../dominio/tipos";
import { BotonBuscar } from "../negocios/BuscadorFichas";
import { EtiquetaEtapa } from "../negocios/EtiquetaEtapa";
import { formatoFecha, formatoFechaHora } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";

/**
 * Agenda como Google Calendar (Robert, 02/10/2026): vistas de mes, semana y día, más la
 * lista por días (vencidos, hoy, mañana…). Un recuadro por consultoría, presentación,
 * siguiente paso o vuelta de Largo plazo de cada proyecto en su día, con el color de su
 * responsable; al pulsarlo se abre la ficha en la ventana. Lo vencido sale en rojo y
 * arriba, para que no se pierda en días pasados.
 */
type Vista = VistaCalendario | "lista";
const VISTAS: Vista[] = ["mes", "semana", "dia", "lista"];

const ICONO: Record<TipoElementoAgenda, typeof Flag> = {
  reunion: CalendarCheck,
  presentacion: Presentation,
  siguiente_paso: Flag,
  vuelta: RotateCcw,
};

const esVencido = (e: ElementoAgenda, hoy: Date) =>
  diasEntre(hoy, diaDe(e.fecha)) < 0;

/** Texto completo de un elemento (lector de pantalla y tooltip). */
const useDescribir = () => {
  const translate = useTranslate();
  const { nombre } = useResponsables();
  return (e: ElementoAgenda) =>
    [
      e.negocio.nombre,
      translate(`nevora.agenda.${e.tipo}`) +
        (e.tipo === "siguiente_paso" && e.titulo !== e.negocio.nombre
          ? `: ${e.titulo}`
          : ""),
      e.conHora ? formatoFechaHora(e.fecha) : formatoFecha(e.fecha),
      nombre(e.negocio.asignado_a) || translate("nevora.negocios.sin_asignar"),
    ].join(" · ");
};

export const AgendaPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const [vista, setVista] = useStore<Vista>(
    "nevora.agenda.vista",
    isMobile ? "dia" : "mes",
  );
  const [referencia, setReferencia] = useState(() => inicioDelDia(new Date()));
  const { data, isPending } = useGetList<Negocio>("proyectos_vista", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "id", order: "ASC" },
    // S10b: solo lo que tiene fecha y nunca los descubiertos de Prospect.
    filter: { en_leads: true, en_agenda: true },
  });
  // S10: Jan y Robert filtran por persona; un setter ya ve solo lo suyo.
  const [persona, setPersona] = useFiltroPersonas();
  const elementos = useMemo(
    () => elementosAgenda(filtrarPorPersonas(data ?? [], persona)),
    [data, persona],
  );
  const porDia = useMemo(() => agruparPorDia(elementos), [elementos]);
  const hoy = inicioDelDia(new Date());
  const vencidos = elementos.filter((e) => esVencido(e, hoy));

  const irADia = (d: Date) => {
    setReferencia(inicioDelDia(d));
    setVista("dia");
  };

  const cuerpo = (
    <div className="space-y-4 pb-8" data-guia="agenda">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {!isMobile ? (
          <h1 className="titulo-pagina">{translate("nevora.agenda.titulo")}</h1>
        ) : null}
        <div
          role="tablist"
          aria-label={translate("nevora.agenda.vistas")}
          className="inline-flex rounded-lg border bg-card p-1 gap-1"
        >
          {VISTAS.map((v) => (
            <button
              key={v}
              type="button"
              role="tab"
              aria-selected={vista === v}
              onClick={() => setVista(v)}
              className={cn(
                "px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                vista === v
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {translate(`nevora.agenda.vista_${v}`)}
            </button>
          ))}
        </div>
        <FiltroPersonasSelect valor={persona} cambiar={setPersona} />
      </div>

      {vista !== "lista" ? (
        <Navegacion
          vista={vista}
          referencia={referencia}
          onCambiar={setReferencia}
        />
      ) : null}
      <Leyenda />

      {isPending ? (
        <p className="text-sm text-muted-foreground py-6" role="status">
          {translate("nevora.agenda.cargando")}
        </p>
      ) : (
        <>
          {vista !== "lista" && vencidos.length ? (
            <Vencidos
              elementos={vencidos}
              onVerTodos={() => setVista("lista")}
            />
          ) : null}
          {vista === "mes" ? (
            <VistaMes referencia={referencia} porDia={porDia} onDia={irADia} />
          ) : vista === "semana" ? (
            <VistaSemana
              referencia={referencia}
              porDia={porDia}
              onDia={irADia}
            />
          ) : vista === "dia" ? (
            <VistaDia elementos={porDia.get(aFechaISO(referencia)) ?? []} />
          ) : (
            <VistaLista elementos={elementos} />
          )}
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">
            {translate("nevora.agenda.titulo")}
          </h1>
          <div className="ml-auto flex items-center gap-1">
            <BotonBuscar />
            <CampanaAvisos plegada alinear="end" />
          </div>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return <div className="mt-2">{cuerpo}</div>;
};

const Navegacion = ({
  vista,
  referencia,
  onCambiar,
}: {
  vista: VistaCalendario;
  referencia: Date;
  onCambiar: (d: Date) => void;
}) => {
  const translate = useTranslate();
  let titulo: string;
  if (vista === "mes") {
    titulo = new Intl.DateTimeFormat("es-ES", {
      month: "long",
      year: "numeric",
    }).format(referencia);
  } else if (vista === "dia") {
    titulo = new Intl.DateTimeFormat("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(referencia);
  } else {
    const dias = diasDeLaSemana(referencia);
    const f = new Intl.DateTimeFormat("es-ES", {
      day: "numeric",
      month: "short",
    });
    titulo = `${f.format(dias[0])} – ${f.format(dias[6])}`;
  }
  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={() => onCambiar(inicioDelDia(new Date()))}
      >
        {translate("nevora.agenda.ir_hoy")}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={translate("nevora.agenda.anterior")}
        onClick={() => onCambiar(desplazar(referencia, vista, -1))}
      >
        <ChevronLeft className="size-5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={translate("nevora.agenda.siguiente")}
        onClick={() => onCambiar(desplazar(referencia, vista, 1))}
      >
        <ChevronRight className="size-5" />
      </Button>
      <h2 className="titulo-seccion first-letter:uppercase" aria-live="polite">
        {titulo}
      </h2>
    </div>
  );
};

/** Color de cada responsable activo. Un setter ve solo lo suyo: sin leyenda (Robert, 09/10). */
const Leyenda = () => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const { activos, color } = useResponsables();
  if (!esAdmin || activos.length < 2) return null;
  return (
    <ul
      className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
      aria-label={translate("nevora.agenda.leyenda")}
    >
      {activos.map((u) => (
        <li key={u.id} className="flex items-center gap-1.5">
          <span
            className="inline-block size-2.5 rounded-full"
            style={{ backgroundColor: color(u.id) }}
            aria-hidden
          />
          {`${u.first_name ?? ""} ${u.last_name ?? ""}`.trim()}
        </li>
      ))}
    </ul>
  );
};

/** Recuadro de un elemento: color del responsable a la izquierda, icono del tipo y nombre. */
const Recuadro = ({
  e,
  detalle = false,
}: {
  e: ElementoAgenda;
  detalle?: boolean;
}) => {
  const translate = useTranslate();
  const abrirFicha = useAbrirFicha();
  const { color } = useResponsables();
  const describir = useDescribir();
  const Icono = ICONO[e.tipo];
  const vencido = esVencido(e, inicioDelDia(new Date()));
  const hora = e.conHora
    ? new Intl.DateTimeFormat("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(e.fecha))
    : null;
  return (
    <button
      type="button"
      onClick={() => abrirFicha(e.negocio.negocio_id, e.negocio.proyecto_id)}
      title={describir(e)}
      aria-label={describir(e)}
      className={cn(
        "flex w-full min-w-0 items-start gap-1.5 rounded-md border-l-4 bg-muted px-1.5 py-1 text-left text-xs transition-colors hover:bg-accent",
        detalle && "px-3 py-2 text-sm",
        vencido && "text-red-700 dark:text-red-400",
      )}
      style={{ borderLeftColor: color(e.negocio.asignado_a) }}
    >
      <Icono
        className={cn("mt-0.5 size-3.5 shrink-0", detalle && "size-4")}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">
          {hora ? `${hora} ` : ""}
          {e.negocio.nombre}
        </span>
        {detalle ? (
          <span
            className={cn(
              "block truncate",
              vencido ? "" : "text-muted-foreground",
            )}
          >
            {translate(`nevora.agenda.${e.tipo}`)}
            {e.tipo === "siguiente_paso" && e.titulo !== e.negocio.nombre
              ? `: ${e.titulo}`
              : ""}
          </span>
        ) : null}
      </span>
    </button>
  );
};

const NOMBRES_DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MAXIMO_EN_CELDA = 3;

const VistaMes = ({
  referencia,
  porDia,
  onDia,
}: {
  referencia: Date;
  porDia: Map<string, ElementoAgenda[]>;
  onDia: (d: Date) => void;
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const { color } = useResponsables();
  const semanas = useMemo(() => semanasDelMes(referencia), [referencia]);
  const hoy = aFechaISO(new Date());
  const formatoDia = new Intl.DateTimeFormat("es-ES", {
    day: "numeric",
    month: "long",
  });
  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="grid grid-cols-7 border-b text-xs font-medium text-muted-foreground">
        {NOMBRES_DIAS.map((d) => (
          <div key={d} className="px-2 py-1.5 text-center">
            {d}
          </div>
        ))}
      </div>
      {semanas.map((semana) => (
        <div
          key={aFechaISO(semana[0])}
          className="grid grid-cols-7 border-b last:border-b-0"
        >
          {semana.map((d) => {
            const clave = aFechaISO(d);
            const delDia = porDia.get(clave) ?? [];
            const otroMes = d.getMonth() !== referencia.getMonth();
            const esHoy = clave === hoy;
            return (
              <div
                key={clave}
                className={cn(
                  "min-w-0 border-r last:border-r-0 p-1 space-y-1",
                  isMobile ? "min-h-14" : "min-h-24",
                  otroMes && "bg-muted/40",
                )}
              >
                <button
                  type="button"
                  onClick={() => onDia(d)}
                  aria-label={`${formatoDia.format(d)}: ${translate(
                    "nevora.agenda.elementos",
                    { smart_count: delDia.length },
                  )}`}
                  className={cn(
                    "flex size-6 items-center justify-center rounded-full text-xs",
                    esHoy
                      ? "bg-foreground font-semibold text-background"
                      : otroMes
                        ? "text-muted-foreground"
                        : "text-foreground",
                  )}
                >
                  {d.getDate()}
                </button>
                {isMobile ? (
                  delDia.length ? (
                    <div className="flex flex-wrap gap-0.5" aria-hidden>
                      {delDia.slice(0, 4).map((e) => (
                        <span
                          key={e.id}
                          className="inline-block size-1.5 rounded-full"
                          style={{
                            backgroundColor: color(e.negocio.asignado_a),
                          }}
                        />
                      ))}
                    </div>
                  ) : null
                ) : (
                  <>
                    {delDia.slice(0, MAXIMO_EN_CELDA).map((e) => (
                      <Recuadro key={e.id} e={e} />
                    ))}
                    {delDia.length > MAXIMO_EN_CELDA ? (
                      <button
                        type="button"
                        onClick={() => onDia(d)}
                        className="px-1.5 text-xs text-muted-foreground hover:text-foreground hover:underline"
                      >
                        {translate("nevora.agenda.mas", {
                          n: delDia.length - MAXIMO_EN_CELDA,
                        })}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

const VistaSemana = ({
  referencia,
  porDia,
  onDia,
}: {
  referencia: Date;
  porDia: Map<string, ElementoAgenda[]>;
  onDia: (d: Date) => void;
}) => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const dias = diasDeLaSemana(referencia);
  const hoy = aFechaISO(new Date());
  const formato = new Intl.DateTimeFormat("es-ES", {
    weekday: "short",
    day: "numeric",
  });
  return (
    <div className={cn("grid gap-2", isMobile ? "grid-cols-1" : "grid-cols-7")}>
      {dias.map((d) => {
        const clave = aFechaISO(d);
        const delDia = porDia.get(clave) ?? [];
        return (
          <section
            key={clave}
            className={cn(
              "min-w-0 rounded-lg border bg-card p-2 space-y-1.5",
              !isMobile && "min-h-48",
              clave === hoy && "border-foreground",
            )}
          >
            <h3>
              <button
                type="button"
                onClick={() => onDia(d)}
                className={cn(
                  "text-sm font-semibold first-letter:uppercase hover:underline",
                  clave === hoy ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {formato.format(d)}
              </button>
            </h3>
            {delDia.length ? (
              delDia.map((e) => <Recuadro key={e.id} e={e} detalle />)
            ) : isMobile ? (
              <p className="text-xs text-muted-foreground">
                {translate("nevora.agenda.vacio_dia")}
              </p>
            ) : null}
          </section>
        );
      })}
    </div>
  );
};

const VistaDia = ({ elementos }: { elementos: ElementoAgenda[] }) => {
  const translate = useTranslate();
  if (!elementos.length) {
    return (
      <p className="text-muted-foreground py-6">
        {translate("nevora.agenda.vacio_dia")}
      </p>
    );
  }
  return (
    <ul className="space-y-2 max-w-2xl">
      {elementos.map((e) => (
        <li key={e.id} className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Recuadro e={e} detalle />
          </div>
          <EtiquetaEtapa etapa={e.negocio.etapa} className="shrink-0" />
        </li>
      ))}
    </ul>
  );
};

/** Lo vencido, siempre a la vista: si no, se pierde en días pasados del calendario. */
const Vencidos = ({
  elementos,
  onVerTodos,
}: {
  elementos: ElementoAgenda[];
  onVerTodos: () => void;
}) => {
  const translate = useTranslate();
  return (
    <section className="rounded-lg border border-red-300 dark:border-red-800 bg-card p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-red-700 dark:text-red-400">
          {translate("nevora.agenda.vencidos")} · {elementos.length}
        </h2>
        <Button variant="ghost" size="sm" onClick={onVerTodos}>
          {translate("nevora.agenda.ver_lista")}
        </Button>
      </div>
      <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
        {elementos.slice(0, 6).map((e) => (
          <Recuadro key={e.id} e={e} detalle />
        ))}
      </div>
    </section>
  );
};

const VistaLista = ({ elementos }: { elementos: ElementoAgenda[] }) => {
  const translate = useTranslate();
  const grupos = useMemo(() => agruparAgenda(elementos), [elementos]);
  if (!elementos.length) {
    return (
      <p className="text-muted-foreground">
        {translate("nevora.agenda.vacio")}
      </p>
    );
  }
  return (
    <div className="space-y-6 max-w-2xl">
      {GRUPOS_AGENDA.filter((g) => grupos[g].length > 0).map((g) => (
        <section key={g} className="space-y-2">
          <h2
            className={cn(
              "etiqueta",
              g === "vencidos"
                ? "text-red-700 dark:text-red-400"
                : "text-muted-foreground",
            )}
          >
            {translate(`nevora.agenda.${g}`)} · {grupos[g].length}
          </h2>
          <ul className="space-y-2">
            {grupos[g].map((e) => (
              <li key={e.id} className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-muted-foreground">
                  {e.conHora
                    ? formatoFechaHora(e.fecha)
                    : formatoFecha(e.fecha)}
                </span>
                <div className="min-w-0 flex-1">
                  <Recuadro e={e} detalle />
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};
